<template lang="pug">
    section.event-step
        el-alert(
            v-if="!loading && !events.length"
            type="warning"
            effect="dark"
            :closable="false"
            :title="$t('There are no events to sell tickets for yet.')"
            :description="$t('An event can only be ticketed once it allows bookings.')"
        )
        template(v-else)
            el-form-item(:label="$t('Event')" prop="event.id")
                el-select.is-full-width(
                    placeholder=""
                    v-model="selectedEventId"
                    :class="{ loading }"
                    :disabled="eventLocked"
                )
                    el-option(
                        v-for="item in events"
                        :key="item.id"
                        :label="item.name"
                        :value="item.id"
                    )

            transition(name="slide-decent-vertical")
                .sessions(v-if="selectedEvent")
                    el-form-item(:label="$t('Dates included in this ticket')" prop="slots")
                        EventSlotPicker(
                            :event-id="selectedEventId"
                            :value="form.slots || []"
                            select-all-when-empty
                            @input="slots => $set(form, 'slots', slots)"
                        )
</template>

<i18n>
{
    "de": {
        "Event": "Veranstaltung",
        "Dates included in this ticket": "Termine, für die das Ticket gilt",
        "Date": "Termin",
        "Until": "bis",
        "Seats left": "Freie Plätze",
        "unlimited": "unbegrenzt",
        "There are no events to sell tickets for yet.": "Es gibt noch keine Veranstaltungen, für die Tickets verkauft werden können.",
        "An event can only be ticketed once it allows bookings.": "Für eine Veranstaltung können erst dann Tickets verkauft werden, wenn sie Anmeldungen zulässt.",
        "This event has no upcoming dates.": "Diese Veranstaltung hat keine kommenden Termine.",
        "Add a date to the event first. A ticket has to admit its holder to something.": "Bitte zuerst einen Termin zur Veranstaltung hinzufügen. Ein Ticket muss für mindestens einen Termin gelten.",
        "One ticket admits its holder to every date selected here. For a course running over several weeks, select them all.": "Ein Ticket gilt für alle hier ausgewählten Termine. Bei einem Kurs über mehrere Wochen also für alle Termine des Kurses.",
        "{count} past or canceled dates are not shown.": "{count} vergangene oder abgesagte Termine werden nicht angezeigt."
    },
    "en": {
        "{count} past or canceled dates are not shown.": "{count} past or canceled dates are not shown."
    }
}
</i18n>

<script>
import _ from 'lodash';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';
import EventTicketApi from '@/extensions/shop/store/api/eventTicket';
import EventSlotPicker from '@/extensions/shop/views/Product/Partials/EventSlotPicker';

export default {
    name: 'EventStep',
    components: {
        EventSlotPicker,
    },
    props: {
        form: {
            type: Object,
            required: true,
        },
        rules: {
            type: Object,
            required: true,
        },
        // Set when the stepper was opened from an event — that event is the reason it is open.
        eventLocked: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            events: [],
            selectedEventId: null,
        };
    },
    computed: {
        setupStore() {
            return useSetupStore(pinia);
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        selectedEvent() {
            if (!this.selectedEventId) return null;
            return _.find(this.events, item => item.id === this.selectedEventId) || null;
        },
        allSlots() {
            return _.get(this.selectedEvent, 'slots') || [];
        },
    },
    watch: {
        /**
         * Only a real change of event invalidates the selection. The step is destroyed and rebuilt
         * every time the merchant pages through the stepper (the transition is mode="out-in"), and
         * load() then assigns the event id again - clearing on that assignment threw away the slots
         * they had just unticked.
         */
        selectedEventId(value, previous) {
            if (previous === null || previous === undefined) return;
            if (value === previous) return;

            this.$set(this.form, 'slots', []);
        },
    },
    methods: {
        async load() {
            this.loading = true;

            try {
                const events = await EventTicketApi.fetchEventsWithSlots(_.get(this.activeCms, 'id'));

                // An event with "Anmeldungen zulassen" off takes no attendees, so a ticket to it
                // could not admit anyone. Offering it would only lead to a refusal on save.
                this.events = _.filter(events, event => event.booking === 'free');

                const preselectedEvent = _.get(this.form, 'event.id');
                if (preselectedEvent) {
                    this.selectedEventId = preselectedEvent;
                } else if (this.events.length === 1) {
                    this.selectedEventId = this.events[0].id;
                }

            } catch (err) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error(err);

                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.loading = false;
            }
        },
    },
    mounted() {
        this.load();
    },
};
</script>

<style lang="sass" scoped>
.el-alert
    margin-bottom: 1rem

.sessions
    // Same hint style the product form uses - mine only set a margin, so it inherited body text
    // size and leading and read as a paragraph rather than a hint.
    .help
        display: block
        margin-bottom: .5rem
        font-size: 12px
        line-height: 1.4
        color: var(--color-text-muted, #909399)

    .is-full
        color: #F56C6C

    // Element pads selection BODY cells 14px but leaves the header at 10px, so the "select all"
    // checkbox sits 4px off from the ones under it.
    ::v-deep .el-table-column--selection .cell
        padding-left: 10px
        padding-right: 10px
</style>
